import Button from "@/components/ui/Button";
import Dropdown from "@/components/ui/DropDown";
import TextField from "@/components/ui/Textfield";
import { PlusIcon, TrashIcon } from "lucide-react";
import { useAdCategoryStore } from "../store/adCategoryStore";
import { JSX } from "react";
import useGlobalStore from "@/store/globalStore";
import { useShallow } from "zustand/react/shallow";

export default function CreateAdCategoriesStepTwo(): JSX.Element {
  const {
    adCategory,
    publisherType,
    publishers,
    setPublisherType,
    updatePublisher,
    addPublisher,
    deletePublisher,
  } = useAdCategoryStore();

  const { setError, showSuccess } = useGlobalStore(
    useShallow((state) => ({
      setError: state.setError,
      showSuccess: state.showSuccess,
    })),
  );

  const handleDeletePublisher = async (index: number) => {
    try {
      await deletePublisher(index);
      if (publishers[index]?.id) {
        showSuccess("Publisher deleted successfully");
      }
    } catch (error: any) {
      setError(error.message ?? "Failed to delete publisher");
    }
  };

  const handlePublisherTypeChange = (newType: string) => {
    try {
      setPublisherType(newType);
    } catch (error: any) {
      setError(error.message ?? "Failed to change publisher type");
    }
  };

  const publisherOptions = [
    { title: "Single Publisher", value: "single_publisher" },
    { title: "User Defined", value: "user_defined" },
    { title: "Multiple Publisher", value: "multiple_publisher" },
  ];

  const handleSingleChange = (value: string) => updatePublisher(0, value);

  return (
    <div className="mt-6">
      <div className="font-bold text-xl mb-6">
        Assign Publishers to {adCategory || "[Ad Category]"}
      </div>
      <hr className="border-[#EA33C0] mb-6" />

      <div className="flex w-full items-start">
        {/* Inputs */}
        <div className="w-2/3 pr-5">
          <div className="text-gray-500 text-sm mb-1">Publisher</div>
          {publisherType === "multiple_publisher" ? (
            publishers.map((pub, index) => (
              <div key={index} className="flex items-center gap-2 mb-2">
                <TextField
                  type="text"
                  placeholder={`Publication Name ${index + 1}`}
                  className="w-full"
                  value={pub ? pub.name : ""}
                  onChange={(e) => updatePublisher(index, e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => handleDeletePublisher(index)}
                  className="text-red-500 hover:text-red-700"
                  title="Remove Publisher"
                >
                  <TrashIcon size={18} />
                </button>
              </div>
            ))
          ) : (
            <TextField
              type="text"
              placeholder="Publication Name"
              className="w-full"
              value={publishers.length != 0 ? publishers[0].name : ""}
              onChange={(e) => handleSingleChange(e.target.value)}
              disabled={publisherType == "user_defined" ? true : false}
            />
          )}
        </div>

        {/* Dropdown with aligned layout */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <div>
              <div className="text-gray-500 text-sm mb-1">Publisher Type</div>
              <Dropdown
                options={publisherOptions}
                selected={publisherType}
                setSelected={handlePublisherTypeChange}
                className="w-60"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add button */}
      {publisherType === "multiple_publisher" && (
        <div className="mt-4">
          <Button
            type="secondary"
            leadingIcon={<PlusIcon />}
            onClick={addPublisher}
          >
            Add Publisher
          </Button>
        </div>
      )}
    </div>
  );
}
